/**
 * Authority form — RTA-style tabbed data entry for the fixed Sales Authorities.
 *
 * The AGENT does everything here: fills every detail across the tabs AND applies
 * their own signature. Then "Review & send" shows the fully-filled PDF for a final
 * check; on confirm the document goes to the VENDOR, who only signs the vendor
 * block via their emailed link. So once sent the status is "Waiting for <vendor>",
 * never "Need to sign" for the agent.
 *
 * Extras (parity with Real Time Agent): licence scan (Vendor tab) + solicitor
 * picker (Legal representation tab).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft, ArrowRight, Send, Check, Loader2, ScanLine, Search, X, AlertCircle, PenLine, Eye, UserCheck, Users,
  Upload, Paperclip, Mail,
} from 'lucide-react';
import esignApi from '../../../api/esign';
import { getContacts } from '../../../api/contact';
import SignatureModal from '../components/SignatureModal';
import AddressAutocomplete from '../../../components/common/AddressAutocomplete';
import { computeMarketingTotal, parseMoney } from '../utils/marketingTotal';
import { estimateRangeWarning } from '../utils/estimateRange';
import { checklistTotal, isOn, priceOf, sanitizePrice } from '../utils/marketingChecklist';
import { companyVendorHiddenKeys, orderVendorQuestions } from '../utils/vendorEntity';

const AUTOSAVE_MS = 900;

export default function AuthorityFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [envelope, setEnvelope] = useState(null);
  const [schema, setSchema] = useState(null);
  const [values, setValues] = useState({});
  const [missing, setMissing] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [saveState, setSaveState] = useState('saved');   // saved | saving | dirty
  const [sending, setSending] = useState(false);

  // agent signature
  const [hasSigBlock, setHasSigBlock] = useState(false);
  const [agentSigned, setAgentSigned] = useState(false);
  const [agentSignature, setAgentSignature] = useState('');
  const [sigModalOpen, setSigModalOpen] = useState(false);

  // review-before-send
  const [reviewing, setReviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [signingNow, setSigningNow] = useState(false);   // "vendor is with me" handover
  const [notifyingConveyancer, setNotifyingConveyancer] = useState(false);
  const [conveyancerNotified, setConveyancerNotified] = useState(false);
  // How many co-vendor tiers to show (2..5). Driven by the "Add another vendor"
  // button + what's already filled — NOT the saved values, because the 2nd
  // vendor's name isn't persisted server-side until their email is added.
  const [maxVendor, setMaxVendor] = useState(2);

  const savedRef = useRef(null);   // snapshot of the values last confirmed saved
  const timerRef = useRef();
  const valuesRef = useRef({});
  valuesRef.current = values;
  // True once the agent types the Marketing expenses total by hand, so the auto
  // "Advertising + Other expenses" sum below stops overriding it (DOMA 13235163189).
  const marketingTotalTouched = useRef(false);
  const missingRef = useRef([]);
  missingRef.current = missing;

  // Amending a previously-completed authority IN PLACE (DOMA 13177543076): the
  // agent edits the change, re-initials, and sends it for the other parties to
  // re-initial. The send path records a revision instead of a plain first send.
  const amending = !!envelope?.amendment_in_progress;
  // Once the amendment has been sent for re-initialing (a revision with no
  // completed_at), the terms are frozen again and the BE refuses edits
  // (amend_service.amendment_sent_pending_reinitial) — so keep the form locked in
  // that window. amendment_in_progress alone can't tell drafting from sent-pending.
  const amendmentSentForReinitial =
    amending && (envelope?.revisions || []).some((r) => !r.completed_at);
  // A completed/sent authority is read-only, EXCEPT while the owner is drafting an
  // in-place amendment (before it's sent), when the changed fields MUST be editable.
  // Keying this off status alone wrongly locked an amendment that was force-completed
  // out of a stuck state (status stays 'completed' while amendment_in_progress is
  // True), showing "amending" and "can no longer be edited" at once (DOMA 13177543076).
  const locked = envelope
    && !['draft', 'pending'].includes(envelope.status)
    && !(amending && !amendmentSentForReinitial);

  const applyForm = (data) => {
    setSchema(data.schema);
    setValues(data.values || {});
    setMissing(data.missing || []);
    setHasSigBlock(!!data.has_agent_signature);
    setAgentSigned(!!data.agent_signed);
    setAgentSignature(data.agent_signature || '');
    // Treat a saved Marketing total that doesn't equal Advertising + Other as a
    // hand-set figure, so the auto-sum never overwrites a deliberate override.
    {
      const sv = data.values || {};
      const total = String(sv.marketing_total ?? '').trim();
      marketingTotalTouched.current =
        !!total && parseMoney(total) !== parseMoney(sv.advertising) + parseMoney(sv.other_expenses);
    }
    // Reveal as many co-vendor tiers as an existing draft already has names for.
    const v = data.values || {};
    setMaxVendor(
      (v.fifth_vendor_name || '').trim() ? 5
        : (v.fourth_vendor_name || '').trim() ? 4
        : (v.third_vendor_name || '').trim() ? 3 : 2,
    );
  };

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [env, form] = await Promise.all([
          esignApi.getEnvelope(id),
          esignApi.getAuthorityForm(id),
        ]);
        if (!live) return;
        setEnvelope(env.data);
        applyForm(form.data);
        savedRef.current = form.data.values || {};   // baseline for dirty-tracking
        setActiveKey(form.data.schema?.sections?.[0]?.key || null);
      } catch (e) {
        if (e?.response?.status === 400) {
          navigate(`/esign/envelope/${id}`, { replace: true });
          return;
        }
        toast.error('Could not load this authority.');
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [id, navigate]);

  const flush = useCallback(async () => {
    clearTimeout(timerRef.current);
    const sent = valuesRef.current;               // exactly what we send this time
    // Nothing changed since the last confirmed save — skip the round-trip.
    if (savedRef.current && shallowEqualValues(sent, savedRef.current)) {
      setSaveState('saved');
      return true;
    }
    setSaveState('saving');
    try {
      const { data } = await esignApi.saveAuthorityForm(id, sent);
      savedRef.current = sent;
      missingRef.current = data.missing || [];
      setMissing(data.missing || []);
      // Merge the server's normalised values back in, but NEVER clobber a field the
      // user kept typing in during the save: only apply the server value where the
      // local value still equals what we just sent. This kills the "reverting text /
      // jumping cursor" autosave interference.
      if (data.values) {
        setValues((v) => {
          const merged = { ...v };
          for (const k of Object.keys(data.values)) {
            if ((v[k] ?? '') === (sent[k] ?? '')) merged[k] = data.values[k];
          }
          return merged;
        });
      }
      // If more keystrokes landed while saving, stay "dirty" so the pending timer saves them.
      setSaveState(shallowEqualValues(valuesRef.current, savedRef.current) ? 'saved' : 'dirty');
      return true;
    } catch {
      setSaveState('dirty');
      toast.error('Could not save your changes.');
      return false;
    }
  }, [id]);

  const setField = useCallback((key, val) => {
    setValues((v) => {
      const next = { ...v, [key]: val };
      // Both ends of the estimated price range are free-typed: the earlier
      // auto 10% snap (DOMA 13214123746 / 13262358216) kept rewriting what the
      // agent entered, so it is gone and an out-of-range top end is flagged by
      // the inline warning below the fields instead (DOMA 13295853736).
      // Marketing expenses total = Advertising + Other expenses (DOMA 13235163189),
      // kept in step whenever either changes (the marketing checklist fills
      // Advertising), until the agent types the total by hand.
      if (key === 'marketing_total') marketingTotalTouched.current = !!String(val || '').trim();
      if ((key === 'advertising' || key === 'other_expenses') && !marketingTotalTouched.current) {
        next.marketing_total = computeMarketingTotal(next.advertising, next.other_expenses);
      }
      return next;
    });
    setSaveState('dirty');
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { flush(); }, AUTOSAVE_MS);
  }, [flush]);

  useEffect(() => () => {
    clearTimeout(timerRef.current);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  // ---- agent signature ----
  const onAdoptSignature = async (dataUrl) => {
    try {
      await esignApi.agentSign(id, dataUrl);
      setAgentSigned(true);
      setAgentSignature(dataUrl);
      toast.success('Signature added.');
    } catch {
      toast.error('Could not save your signature.');
    }
  };
  const removeSignature = async () => {
    try {
      await esignApi.clearAgentSignature(id);
      setAgentSigned(false);
      setAgentSignature('');
    } catch {
      toast.error('Could not remove the signature.');
    }
  };

  // ---- review-before-send ----
  const openReview = async () => {
    const ok = await flush();
    if (!ok) return;
    if (missingRef.current.length) {
      toast.error(`Please complete ${missingRef.current.length} required field(s).`);
      setActiveKey(sectionKeyOfQuestion(schema, missingRef.current[0].key) || activeKey);
      return;
    }
    if (hasSigBlock && !agentSigned) {
      toast.error('Please add your agent signature before sending.');
      return;
    }
    setPreviewLoading(true);
    setReviewing(true);
    try {
      const { data } = await esignApi.previewBlob(id);
      const url = URL.createObjectURL(data);
      setPreviewUrl((old) => { if (old) URL.revokeObjectURL(old); return url; });
    } catch {
      toast.error('Could not build the preview.');
      setReviewing(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmSend = async () => {
    setSending(true);
    try {
      if (amending) {
        // In-place amendment: record the revision + receipt, re-initial as the
        // agent, and send the other parties to re-initial just the change.
        await esignApi.amendSend(id, agentSignature);
        toast.success('Amendment sent for the other parties to re-initial.');
      } else {
        await esignApi.send(id);
        toast.success('Sent to the vendor for signature.');
      }
      navigate('/esign/agreements');
    } catch (e) {
      toast.error(e?.response?.data?.error
        || (amending ? 'Could not send the amendment.' : 'Could not send the authority.'));
    } finally {
      setSending(false);
    }
  };

  const cancelAmendment = async () => {
    setSending(true);
    try {
      await esignApi.amendCancel(id);
      toast.success('Amendment cancelled.');
      navigate('/esign/agreements');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not cancel the amendment.');
      setSending(false);
    }
  };

  // Email the chosen conveyancer the "please prepare documents" notice. Flush any
  // pending edits first so the server sends using the conveyancer details just typed.
  const notifyConveyancer = async () => {
    setNotifyingConveyancer(true);
    try {
      const ok = await flush();
      if (!ok) { setNotifyingConveyancer(false); return; }
      const { data } = await esignApi.notifyConveyancer(id);
      setConveyancerNotified(true);
      toast.success(`Sent to the conveyancer (${data.to}).`);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not email the conveyancer.');
    } finally {
      setNotifyingConveyancer(false);
    }
  };

  // "Vendor is with me — sign now": send without emailing, then hand this device
  // straight to the vendor's signing page so they sign on the spot (the RTA / kitchen-
  // table moment). Falls back to a friendly message if no signer token comes back.
  const signInPerson = async () => {
    setSigningNow(true);
    try {
      const { data } = await esignApi.send(id, { inPerson: true });
      if (data?.sign_token) {
        // `agent=1` tells the signing page this is an in-person handover on the
        // agent's device, so it can offer a way back to the CRM once done.
        navigate(`/esign/sign/${data.sign_token}?agent=1`);
      } else {
        toast.success('Sent — open the vendor’s link to sign.');
        navigate('/esign/agreements');
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not start in-person signing.');
      setSigningNow(false);
    }
  };

  // RTA-style launcher: "generate" the authority (freeze it, no emails) then open
  // the signing screen where each party (agent / vendor / co-vendor) can be signed
  // in the room or sent offsite individually.
  const manageSigning = async () => {
    setSigningNow(true);
    try {
      await esignApi.generate(id);
      navigate(`/esign/document/${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not prepare signing.');
      setSigningNow(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-[60vh] text-gray-400">
      <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>;
  }
  if (!schema) return null;

  // ---- Review screen ----
  if (reviewing) {
    return (
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Stacks on phone/tablet (the action buttons overflowed and crushed the
            heading to one word per line on iPad - DOMA 13245395976); side-by-side
            from lg up where there is room. */}
        <div className="flex flex-col gap-3 mb-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => setReviewing(false)}
              className="p-2 rounded-lg border text-teal-600 hover:bg-teal-50 shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <h1 className="text-lg font-semibold truncate">Review before sending</h1>
              <p className="text-xs text-gray-500">Check every detail is filled in. Go back to change anything.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 lg:justify-end">
            <button onClick={() => setReviewing(false)}
              className="px-3 py-2 text-sm rounded-lg border hover:bg-gray-50">Back to form</button>
            {/* The "signing options" / "sign in person" launchers do a first send,
                not an amendment, so they're hidden while amending. */}
            {!amending && (
              <>
                <button onClick={manageSigning} disabled={sending || signingNow || previewLoading}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg border-2 border-teal-600 text-teal-700 hover:bg-teal-50 disabled:opacity-50">
                  {signingNow ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                  Signing options
                </button>
                <button onClick={signInPerson} disabled={sending || signingNow || previewLoading}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg border-2 border-teal-600 text-teal-700 hover:bg-teal-50 disabled:opacity-50">
                  {signingNow ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />}
                  <span className="hidden sm:inline">Vendor is with me — </span>Sign now
                </button>
              </>
            )}
            <button onClick={confirmSend} disabled={sending || signingNow || previewLoading}
              className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {amending ? 'Send amendment' : 'Email to vendor'}
            </button>
          </div>
        </div>
        <div className="bg-white border rounded-xl overflow-hidden" style={{ height: '85vh' }}>
          {previewLoading || !previewUrl
            ? <div className="flex items-center justify-center h-full text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin mr-2" /> Building preview…</div>
            : <iframe src={previewUrl} title="Document preview" className="w-full h-full" />}
        </div>
      </div>
    );
  }

  const section = schema.sections.find((s) => s.key === activeKey) || schema.sections[0];
  // Next tab, so the agent can page through the sections from the bottom of the
  // form instead of hunting for the tab strip up top (DOMA 13213993817).
  // Undefined on the final section, where "Review & send" is the forward action.
  const nextSection = schema.sections[schema.sections.findIndex((s) => s.key === section.key) + 1];
  const requiredKeys = new Set(missing.map((m) => m.key));
  const ownerName = envelope?.owner_name || (envelope?.recipients || [])[0]?.name || '';

  // The estimated price is quoted as EITHER a single amount OR a range — show only
  // the amount fields that match the chosen mode (default: single).
  const hidden = new Set();
  if (values.estimate_mode === 'range') hidden.add('estimate_single');
  else { hidden.add('estimate_low'); hidden.add('estimate_high'); }

  // Flag (never block) a range whose top end is outside 10% of the bottom end
  // (DOMA 13295853736). Shown under the grid of the section holding the fields.
  const rangeWarning = values.estimate_mode === 'range'
    && section.questions.some((q) => q.key === 'estimate_low')
    ? estimateRangeWarning(values.estimate_low, values.estimate_high)
    : '';

  // Progressive vendor reveal driven by the "Add another vendor" button
  // (local maxVendor state), so the vendor tab isn't cluttered with five blank
  // vendors (ticket 12525266778 supports up to 5, but most sales have 1-2).
  if (maxVendor < 3) ['third_vendor_name', 'third_vendor_email', 'third_vendor_mobile'].forEach((k) => hidden.add(k));
  if (maxVendor < 4) ['fourth_vendor_name', 'fourth_vendor_email', 'fourth_vendor_mobile'].forEach((k) => hidden.add(k));
  if (maxVendor < 5) ['fifth_vendor_name', 'fifth_vendor_email', 'fifth_vendor_mobile'].forEach((k) => hidden.add(k));

  // Company vendor (Genneva, CRM-2026-00104): hide the entity toggle from the
  // grid (it renders on its own above), and swap the individual "Vendor name"
  // for the company fields (or vice-versa) based on the toggle.
  companyVendorHiddenKeys(values).forEach((k) => hidden.add(k));
  const vendorEntityQ = section.questions.find((q) => q.key === 'vendor_entity');
  const sectionQuestions = section.key === 'vendor'
    ? orderVendorQuestions(section.questions, values)
    : section.questions;

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs text-gray-400">
          Authorities <span className="mx-1">›</span> {schema.name}
        </div>
        <SaveBadge state={saveState} />
      </div>
      <div className="flex flex-col gap-3 mb-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate('/esign/agreements')}
            className="p-2 rounded-lg border text-teal-600 hover:bg-teal-50 shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold truncate">{envelope?.title || schema.name}</h1>
            <p className="text-xs text-gray-500 truncate">
              {(envelope?.recipients || []).map((r) => r.name || r.email).filter(Boolean).join(' · ')}
            </p>
          </div>
        </div>
        {!locked && (
          <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
            {amending && (
              <button onClick={cancelAmendment} disabled={sending}
                className="px-3 py-2 text-sm rounded-lg border hover:bg-gray-50 disabled:opacity-50">
                Cancel amendment
              </button>
            )}
            <button onClick={openReview} disabled={sending}
              className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50">
              <Eye className="w-4 h-4" /> {amending ? 'Review & send amendment' : 'Review & send'}
            </button>
          </div>
        )}
      </div>

      {amending && (
        <div className="mb-4 flex items-start gap-2 text-sm bg-blue-50 text-blue-800 border border-blue-200 rounded-lg px-3 py-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>You're amending this authority in place. Change what's needed, then review and send: the
            change is recorded on a Revisions page and the other parties re-initial just the change
            (their existing signatures are kept).</span>
        </div>
      )}

      {locked && (
        <div className="mb-4 flex items-center gap-2 text-sm bg-amber-50 text-amber-800 border border-amber-200 rounded-lg px-3 py-2">
          <AlertCircle className="w-4 h-4" /> This authority is {envelope.status} and can no longer be edited.
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b overflow-x-auto mb-6">
        {schema.sections.map((s) => {
          const nMissing = missing.filter((m) => sectionKeyOfQuestion(schema, m.key) === s.key).length;
          return (
            <button key={s.key} onClick={() => setActiveKey(s.key)}
              className={`relative px-4 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${
                s.key === section.key
                  ? 'border-teal-600 text-teal-700 font-semibold'
                  : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {s.title}
              {nMissing > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-500 text-white text-[10px]">
                  {nMissing}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Section panel */}
      <div className="bg-white border rounded-xl p-5 md:p-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h2 className="text-lg font-semibold">{section.title}</h2>
          {section.key === 'vendor' && !locked && (
            <LicenceScanButton id={id} onScanned={(f) => {
              Object.entries(f).forEach(([k, val]) => { if (val) setField(k, val); });
            }} />
          )}
        </div>

        {section.key === 'vendor' && vendorEntityQ && (
          <div className="mb-5 max-w-sm">
            <Field q={vendorEntityQ} value={values.vendor_entity || 'individual'} disabled={locked}
              envelopeId={id}
              onChange={(val) => setField('vendor_entity', val)}
              onSetField={setField} />
          </div>
        )}

        {section.key === 'legal' && !locked && (
          <LegalRepPicker onPick={(c) => {
            if (c.name) setField('legal_rep_name', c.name);
            if (c.company || c.firm) setField('legal_rep_firm', c.company || c.firm);
            if (c.address) setField('legal_rep_address', c.address);
            if (c.phone) setField('legal_rep_phone', c.phone);
            if (c.email) setField('legal_rep_email', c.email);
          }} />
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {sectionQuestions.filter((q) => !hidden.has(q.key)).map((q) => (
            <Field key={q.key} q={q} value={values[q.key] || ''} disabled={locked}
              invalid={requiredKeys.has(q.key)} envelopeId={id}
              onChange={(val) => setField(q.key, val)}
              onSetField={setField} />
          ))}
        </div>
        {rangeWarning && (
          <div className="mt-4 flex items-start gap-2 text-sm bg-amber-50 text-amber-800 border border-amber-200 rounded-lg px-3 py-2">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{rangeWarning} You can still send the authority, but please check the estimate.</span>
          </div>
        )}
        {section.key === 'vendor' && !locked && maxVendor < 5 && (
          <button type="button" onClick={() => setMaxVendor((m) => Math.min(5, m + 1))}
            className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-dashed border-teal-400 text-teal-700 hover:bg-teal-50">
            + Add another vendor
          </button>
        )}
        {section.key === 'legal' && !locked && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button type="button" onClick={notifyConveyancer}
              disabled={notifyingConveyancer || !(values.legal_rep_email || '').trim()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-teal-500 text-teal-700 hover:bg-teal-50 disabled:opacity-50 disabled:cursor-not-allowed">
              {notifyingConveyancer ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              {conveyancerNotified ? 'Notify conveyancer again' : 'Notify conveyancer'}
            </button>
            {conveyancerNotified && (
              <span className="inline-flex items-center gap-1 text-sm text-green-700">
                <Check className="w-4 h-4" /> Sent
              </span>
            )}
            {!(values.legal_rep_email || '').trim() && (
              <span className="text-xs text-gray-400">Add the conveyancer’s email to enable</span>
            )}
          </div>
        )}
        {nextSection && (
          <div className="mt-6 flex justify-end border-t pt-4">
            <button type="button" onClick={() => setActiveKey(nextSection.key)}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
              Next: {nextSection.title} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Agent signature */}
      {hasSigBlock && (
        <div className="bg-white border rounded-xl p-5 md:p-6 mt-4">
          <h2 className="text-lg font-semibold mb-1">Your signature (agent)</h2>
          <p className="text-sm text-gray-500 mb-3">
            Sign here to complete your part. The vendor signs their own section after you send.
          </p>
          {agentSigned && agentSignature ? (
            <div className="flex flex-wrap items-center gap-4">
              <div className="border rounded-lg bg-gray-50 px-4 py-2 h-16 flex items-center">
                {agentSignature.startsWith('data:')
                  ? <img src={agentSignature} alt="Your signature" className="max-h-12 object-contain" />
                  : <span className="text-lg" style={{ fontFamily: 'Dancing Script, cursive' }}>
                      {agentSignature.replace(/^type:/, '')}</span>}
              </div>
              <span className="inline-flex items-center gap-1 text-sm text-green-700">
                <Check className="w-4 h-4" /> Signed
              </span>
              {!locked && (
                <button onClick={removeSignature} className="text-sm text-gray-500 hover:text-red-600">Re-sign</button>
              )}
            </div>
          ) : (
            !locked && (
              <button onClick={() => setSigModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 border-dashed border-teal-400 text-teal-700 hover:bg-teal-50">
                <PenLine className="w-4 h-4" /> Add your signature
              </button>
            )
          )}
        </div>
      )}

      {/* Footer status */}
      <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
        <span>
          {missing.length === 0 && (!hasSigBlock || agentSigned)
            ? <span className="text-green-600 inline-flex items-center gap-1"><Check className="w-4 h-4" /> Ready to send</span>
            : `${missing.length} required field(s)${hasSigBlock && !agentSigned ? ' + your signature' : ''} to complete`}
        </span>
        <SaveBadge state={saveState} />
      </div>

      <SignatureModal open={sigModalOpen} onClose={() => setSigModalOpen(false)}
        onAdopt={onAdoptSignature}
        defaultName={ownerName} sealId={String(envelope?.uid || id)} />
    </div>
  );
}

// --- helpers ---
// Shallow value-map equality (treats missing key and "" as equal) — used so autosave
// only fires on a real change and never clobbers in-flight keystrokes.
function shallowEqualValues(a, b) {
  if (!a || !b) return false;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    if ((a[k] ?? '') !== (b[k] ?? '')) return false;
  }
  return true;
}

function sectionKeyOfQuestion(schema, qkey) {
  for (const s of schema.sections) {
    if (s.questions.some((q) => q.key === qkey)) return s.key;
  }
  return null;
}

function SaveBadge({ state }) {
  if (state === 'saving') return <span className="text-xs text-gray-400 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>;
  if (state === 'dirty') return <span className="text-xs text-amber-600">Unsaved changes</span>;
  return <span className="text-xs text-gray-400 inline-flex items-center gap-1"><Check className="w-3 h-3" /> Saved</span>;
}

function Field({ q, value, onChange, disabled, invalid, envelopeId, onSetField }) {
  const base = `w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40 ${
    invalid ? 'border-red-400' : 'border-gray-300'} ${disabled ? 'bg-gray-50 text-gray-500' : ''}`;
  const common = { value, disabled, onChange: (e) => onChange(e.target.value), className: base };

  // Marketing sheet checklist (Monday 13068472367): tick the package items,
  // edit any price; the ticked total auto-fills the Advertising field.
  if (q.type === 'marketing_checklist') {
    return (
      <div className="md:col-span-2 block">
        <span className="text-xs font-semibold text-gray-600">{q.label}</span>
        <MarketingChecklist
          q={q} value={value} disabled={disabled}
          onChange={onChange}
          onTotal={(total) => onSetField && onSetField('advertising', total)}
        />
      </div>
    );
  }

  // Performance-based commission (Monday 13272089653): optional tiered structure —
  // one rate up to a sale price, a different rate above it. Prints on an appended
  // page; empty by default so the standard commission fields are unaffected.
  if (q.type === 'performance_commission') {
    return (
      <div className="md:col-span-2 block">
        <span className="block text-sm font-medium text-gray-700 mb-1.5">{q.label}</span>
        <PerformanceCommission value={value} disabled={disabled} onChange={onChange} envelopeId={envelopeId} />
        {q.help && <span className="block text-xs text-gray-400 mt-1">{q.help}</span>}
      </div>
    );
  }

  // Either/or question: mutually-exclusive pills. Clicking the selected one again
  // clears it. Exactly one (or none) can be chosen — never both.
  if (q.type === 'choice') {
    return (
      <div className="md:col-span-2 block">
        <span className="block text-sm font-medium text-gray-700 mb-1.5">
          {q.label}{q.required && <span className="text-red-500"> *</span>}
        </span>
        <div className="flex flex-wrap gap-2">
          {(q.options || []).map((o) => {
            const on = value === o.value;
            return (
              <button type="button" key={o.value} disabled={disabled}
                onClick={() => onChange(on ? '' : o.value)}
                className={`px-4 py-2 text-sm rounded-lg border transition-colors ${
                  on ? 'border-teal-600 bg-teal-50 text-teal-700 font-semibold'
                     : 'border-gray-300 text-gray-600 hover:bg-gray-50'} ${
                  disabled ? 'opacity-60 cursor-not-allowed' : ''}`}>
                {o.label}
              </button>
            );
          })}
        </div>
        {q.help && <span className="block text-xs text-gray-400 mt-1">{q.help}</span>}
      </div>
    );
  }

  // Supporting document upload (e.g. the invoice/quote backing "Marketing
  // expenses total") — a real file, stored against the envelope for the
  // agency's own record. Never printed on the authority and never shown to
  // the vendor (capture_only), so it manages its own attachments list rather
  // than routing through the string `value`/autosave used by every other
  // question type.
  if (q.type === 'file') {
    return (
      <div className="md:col-span-2 block">
        <span className="block text-sm font-medium text-gray-700 mb-1.5">
          {q.label}{q.required && <span className="text-red-500"> *</span>}
        </span>
        <FileDropQuestion envelopeId={envelopeId} fieldKey={q.key} disabled={disabled} />
        {q.help && <span className="block text-xs text-gray-400 mt-1">{q.help}</span>}
      </div>
    );
  }

  // Property address: the same GNAF-backed autocomplete used when adding a
  // property, so matches drop down as the agent types instead of a plain text box
  // (DOMA 13225536711). Picking a suggestion fills the full address (unit incl.).
  // Only while editable - a locked (already-signed) authority falls through to the
  // read-only text input below, since the autocomplete has no disabled state.
  if (q.key === 'property_address' && !disabled) {
    return (
      <div className="block">
        <span className="block text-sm font-medium text-gray-700 mb-1">
          {q.label}{q.required && <span className="text-red-500"> *</span>}
        </span>
        <AddressAutocomplete
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onSelect={(s) => onChange(s.description)}
          placeholder={q.help || 'Start typing the property address...'}
          className={base}
        />
        {q.help && <span className="block text-xs text-gray-400 mt-1">{q.help}</span>}
      </div>
    );
  }

  let input;
  if (q.type === 'day_multiselect') {
    input = <DayMultiSelect options={q.options || []} value={value} disabled={disabled} onChange={onChange} className={base} />;
  } else if (q.type === 'textarea') {
    input = (
      <div>
        {q.presets && q.presets.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-1.5">
            {q.presets.map((preset) => (
              <button type="button" key={preset} disabled={disabled}
                onClick={() => onChange(value ? `${value}\n${preset}` : preset)}
                className="px-2.5 py-1 text-xs rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed">
                {preset}
              </button>
            ))}
          </div>
        )}
        <textarea rows={2} {...common} />
      </div>
    );
  } else if (q.type === 'date') {
    input = <input type="date" {...common} />;
  } else if (q.type === 'time') {
    input = <input type="time" {...common} />;
  } else if (q.type === 'currency') {
    // Show thousands-separator commas as the agent types ($120,000, not $120000
    // — CG, ticket 12525266778) while STORING the raw number, so the PDF bake and
    // the commission maths never see a comma.
    const withCommas = (v) => {
      const raw = String(v ?? '').replace(/,/g, '');
      const m = /^(\d+)(\.\d*)?$/.exec(raw);
      // eslint-disable-next-line no-restricted-syntax -- number grouping, not a date/time
      return m ? Number(m[1]).toLocaleString('en-US') + (m[2] || '') : raw;
    };
    input = (
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
        <input type="text" inputMode="decimal" disabled={disabled}
          value={withCommas(value)}
          onChange={(e) => onChange(e.target.value.replace(/,/g, ''))}
          className={base + ' pl-6'} />
      </div>
    );
  } else if (q.type === 'number') {
    input = <input type="text" inputMode="numeric" {...common} />;
  } else if (q.type === 'email') {
    input = <input type="email" {...common} />;
  } else if (q.type === 'tel') {
    input = <input type="tel" {...common} />;
  } else {
    input = <input type="text" {...common} />;
  }

  return (
    <label className={['textarea', 'day_multiselect'].includes(q.type) ? 'md:col-span-2 block' : 'block'}>
      <span className="block text-sm font-medium text-gray-700 mb-1">
        {q.label}{q.required && <span className="text-red-500"> *</span>}
      </span>
      {input}
      {q.help && <span className="block text-xs text-gray-400 mt-1">{q.help}</span>}
    </label>
  );
}

// Multiple preset day-values can be ticked at once (RTA reference: 30/45/60/
// 90/120), plus a free-text custom entry - joined into one comma-separated
// string, same single-string contract as every other question (CG,
// 2026-08-06: "make this an option to select 30, 45, 60 90, 120 days...
// More then 1 option is possible to tick").
/**
 * The Collings Price List as a tickable checklist with editable prices
 * (Monday 13068472367). State is a JSON object in form_state under the
 * question's own key: { [itemKey]: { on: bool, price: "403" } } — a price is
 * only stored once it differs from the schema default, so a later default
 * change flows through to untouched authorities. The ticked total is pushed
 * up via onTotal so it auto-fills the "Advertising" field (which in turn feeds
 * the "Marketing expenses total" = Advertising + Other expenses).
 */
function MarketingChecklist({ q, value, disabled, onChange, onTotal }) {
  const items = q.items || [];
  let saved = {};
  try { saved = JSON.parse(value || '{}') || {}; } catch { saved = {}; }

  const update = (next) => {
    onChange(JSON.stringify(next));
    const total = checklistTotal(items, next);
    onTotal(total ? String(Math.round(total * 100) / 100) : '');
  };
  const toggle = (it) => update({ ...saved, [it.key]: { ...(saved[it.key] || {}), on: !isOn(it, saved) } });
  const setPrice = (it, raw) => update({
    ...saved, [it.key]: { ...(saved[it.key] || {}), price: sanitizePrice(raw) },
  });

  const total = checklistTotal(items, saved);
  const groups = [];
  items.forEach((it) => {
    const g = groups.find((x) => x.name === it.group);
    if (g) g.items.push(it);
    else groups.push({ name: it.group, items: [it] });
  });

  return (
    <div className="mt-1 border border-gray-200 rounded-xl p-4 bg-gray-50/50">
      {groups.map((group) => (
        <div key={group.name} className="mb-3 last:mb-0">
          <p className="text-[11px] font-bold uppercase tracking-wide text-teal-700 mb-1.5">{group.name}</p>
          <div className="space-y-1">
            {group.items.map((it) => (
              <label key={it.key}
                className={`flex items-center gap-2.5 rounded-lg px-2 py-1 ${disabled ? '' : 'cursor-pointer hover:bg-white'}`}>
                <input
                  type="checkbox" checked={isOn(it, saved)} disabled={disabled}
                  onChange={() => toggle(it)}
                  className="w-4 h-4 accent-teal-600 flex-shrink-0"
                />
                <span className={`flex-1 text-sm ${isOn(it, saved) ? 'text-gray-900' : 'text-gray-600'}`}>{it.label}</span>
                <span className="text-xs text-gray-400">$</span>
                <input
                  type="text" inputMode="decimal" disabled={disabled}
                  value={priceOf(it, saved)} placeholder={it.price == null ? 'varies' : ''}
                  onClick={(e) => e.preventDefault()}
                  onChange={(e) => setPrice(it, e.target.value)}
                  className="w-24 border border-gray-300 rounded-lg px-2 py-1 text-sm text-right focus:outline-none focus:ring-2 focus:ring-teal-500/40 bg-white"
                />
              </label>
            ))}
          </div>
        </div>
      ))}
      <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-700">Selected items total</span>
        <span className="text-base font-bold text-gray-900">
          ${(total || 0).toLocaleString('en-AU', { maximumFractionDigits: 2 })}
        </span>
      </div>
      <p className="mt-1 text-[11px] text-gray-400">
        Fills the Advertising field below (Marketing expenses total = Advertising + Other expenses).
      </p>
    </div>
  );
}


// The 9 Real Time Agent "Commission calculated by" methods (Monday 13272089653).
const PC_METHODS = [
  { value: 'brackets', label: 'Brackets' },
  { value: 'sliding_scale', label: 'Sliding scale' },
  { value: 'stepped', label: 'Stepped' },
  { value: 'combo', label: 'Combo' },
  { value: 'vendor_reserve', label: "Vendor's Reserve" },
  { value: 'fixed_amount_reserve', label: 'Fixed Amount + Reserve' },
  { value: 'fixed_percentage_reserve', label: 'Fixed Percentage + Reserve' },
  { value: 'vendor_discretion', label: "Vendor's discretion" },
  { value: 'upload', label: 'Upload your own' },
];
const PC_TIER_METHODS = ['brackets', 'sliding_scale', 'stepped', 'combo'];

function pcNum(x) {
  const n = Number(String(x ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : 0;
}
function pcMoney(n) {
  if (!Number.isFinite(n)) return '';
  const r = Math.round(n * 100) / 100;
  const [i, d] = r.toFixed(2).split('.');
  const withSep = i.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return d === '00' ? withSep : `${withSep}.${d}`;
}

// Estimated commission at the entered rates for a sale price. Returns a number, a
// {low, high} range (vendor's discretion), or null when it can't be computed.
function pcEstimate(d, price) {
  const p = pcNum(price);
  if (p <= 0) return null;
  const tiers = (d.tiers || []).filter((t) => t && String(t.rate || '').trim());
  switch (d.method) {
    case 'brackets': {
      let rate = 0; let matched = false;
      for (const t of tiers) {
        if (String(t.up_to || '').trim() && p <= pcNum(t.up_to)) { rate = pcNum(t.rate); matched = true; break; }
      }
      if (!matched) {
        const above = tiers.find((t) => !String(t.up_to || '').trim());
        rate = above ? pcNum(above.rate) : (tiers.length ? pcNum(tiers[tiers.length - 1].rate) : 0);
      }
      return (p * rate) / 100;
    }
    case 'sliding_scale': {
      const sorted = [...tiers].sort((a, b) => (pcNum(a.up_to) || Infinity) - (pcNum(b.up_to) || Infinity));
      let prev = 0; let c = 0;
      for (const t of sorted) {
        const up = String(t.up_to || '').trim() ? pcNum(t.up_to) : Infinity;
        const top = Math.min(p, up);
        if (top > prev) { c += ((top - prev) * pcNum(t.rate)) / 100; prev = top; }
      }
      return c;
    }
    case 'stepped':
    case 'combo': {
      let c = (p * pcNum(d.base_rate)) / 100;
      for (const t of tiers) { const ab = pcNum(t.up_to); if (p > ab) c += ((p - ab) * pcNum(t.rate)) / 100; }
      return c;
    }
    case 'vendor_reserve': {
      const res = pcNum(d.reserve);
      return (Math.min(p, res) * pcNum(d.base_rate)) / 100 + (Math.max(0, p - res) * pcNum(d.bonus_rate)) / 100;
    }
    case 'fixed_amount_reserve': {
      const res = pcNum(d.reserve);
      return pcNum(d.base_amount) + (Math.max(0, p - res) * pcNum(d.bonus_rate)) / 100;
    }
    case 'fixed_percentage_reserve': {
      const res = pcNum(d.reserve);
      return (p * pcNum(d.base_rate)) / 100 + (Math.max(0, p - res) * pcNum(d.bonus_rate)) / 100;
    }
    case 'vendor_discretion':
      return { low: (p * pcNum(d.lower)) / 100, high: (p * pcNum(d.upper)) / 100 };
    default:
      return null;
  }
}

function PerformanceCommission({ value, disabled, onChange, envelopeId }) {
  let d = {};
  try {
    const parsed = JSON.parse(value || '{}');
    if (Array.isArray(parsed)) d = { method: 'brackets', tiers: parsed };  // legacy 2-tier
    else if (parsed && typeof parsed === 'object') d = parsed;
  } catch { d = {}; }
  const method = d.method || '';
  const gst = d.gst || 'inc';
  const tiers = Array.isArray(d.tiers) && d.tiers.length ? d.tiers : [{ rate: '', up_to: '' }, { rate: '', up_to: '' }];

  const num = (raw) => String(raw ?? '').replace(/[^0-9.]/g, '');
  const commit = (next) => onChange(JSON.stringify(next));
  const setField = (patch) => commit({ ...d, ...patch });
  const setTier = (i, patch) => commit({ ...d, tiers: tiers.map((t, j) => (j === i ? { ...t, ...patch } : t)) });
  const addTier = () => commit({ ...d, tiers: [...tiers, { rate: '', up_to: '' }] });
  const removeTier = (i) => { if (tiers.length > 1) commit({ ...d, tiers: tiers.filter((_, j) => j !== i) }); };

  const field = 'w-24 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-teal-500/40 bg-white disabled:bg-gray-50';
  const rateInput = (val, on, label) => (
    <input type="text" inputMode="decimal" disabled={disabled} value={val || ''}
      onChange={(e) => on(num(e.target.value))} aria-label={label} placeholder="0"
      className="w-16 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-teal-500/40 bg-white disabled:bg-gray-50" />
  );
  const dollarInput = (val, on, label, ph) => (
    <span className="inline-flex items-center gap-1"><span className="text-xs text-gray-400">$</span>
      <input type="text" inputMode="decimal" disabled={disabled} value={val || ''}
        onChange={(e) => on(num(e.target.value))} aria-label={label} placeholder={ph || ''} className={field} /></span>
  );

  const tierRows = (aboveOnly) => (
    <>
      {tiers.map((t, i) => (
        <div key={i} className="flex items-center gap-2 flex-wrap">
          {rateInput(t.rate, (v) => setTier(i, { rate: v }), 'Rate (%)')}
          <span className="text-sm text-gray-500">% {aboveOnly ? 'if sold above' : 'of the sale price up to'}&nbsp;</span>
          {dollarInput(t.up_to, (v) => setTier(i, { up_to: v }), 'Threshold ($)', '800000')}
          {tiers.length > 1 && !disabled && (
            <button type="button" onClick={() => removeTier(i)}
              className="ml-auto text-xs text-gray-400 hover:text-red-500" aria-label="Remove range">Remove</button>
          )}
        </div>
      ))}
      {!disabled && (
        <button type="button" onClick={addTier}
          className="text-sm font-medium text-teal-700 hover:text-teal-800">+ Add another range</button>
      )}
      {!aboveOnly && (
        <p className="text-[11px] text-gray-400">Leave the last range&rsquo;s amount blank for &ldquo;and above&rdquo;.</p>
      )}
    </>
  );

  const est = method && method !== 'upload' ? pcEstimate(d, d.example) : null;
  const estIncGST = (n) => (gst === 'exc' ? n * 1.1 : n);

  return (
    <div className="mt-1 border border-gray-200 rounded-xl p-4 bg-gray-50/50 space-y-3">
      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm">
          <span className="text-gray-600">Method</span>
          <select disabled={disabled} value={method} onChange={(e) => commit({ ...d, method: e.target.value })}
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/40 disabled:bg-gray-50">
            <option value="">Standard commission (none)</option>
            {PC_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </label>
        {method && (
          <span className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm">
            {[['inc', 'Including GST'], ['exc', 'Excluding GST']].map(([v, lbl]) => (
              <button key={v} type="button" disabled={disabled} onClick={() => setField({ gst: v })}
                className={`px-3 py-1.5 ${gst === v ? 'bg-teal-600 text-white font-medium' : 'bg-white text-gray-600'}`}>{lbl}</button>
            ))}
          </span>
        )}
      </div>

      {PC_TIER_METHODS.includes(method) && (method === 'stepped' || method === 'combo') && (
        <div className="flex items-center gap-2 flex-wrap">
          {rateInput(d.base_rate, (v) => setField({ base_rate: v }), 'Base rate (%)')}
          <span className="text-sm text-gray-500">% of the sale price, plus:</span>
        </div>
      )}
      {PC_TIER_METHODS.includes(method) && tierRows(method === 'stepped' || method === 'combo')}

      {method === 'vendor_reserve' && (
        <>
          <div className="flex items-center gap-2 flex-wrap">{rateInput(d.base_rate, (v) => setField({ base_rate: v }), 'Base rate (%)')}
            <span className="text-sm text-gray-500">% of the sale price up to the reserve of</span>{dollarInput(d.reserve, (v) => setField({ reserve: v }), 'Reserve ($)', '800000')}</div>
          <div className="flex items-center gap-2 flex-wrap"><span className="text-sm text-gray-500">plus</span>{rateInput(d.bonus_rate, (v) => setField({ bonus_rate: v }), 'Bonus rate (%)')}
            <span className="text-sm text-gray-500">% of the sale price above the reserve</span></div>
        </>
      )}
      {method === 'fixed_amount_reserve' && (
        <>
          <div className="flex items-center gap-2 flex-wrap"><span className="text-sm text-gray-500">Fixed commission of</span>{dollarInput(d.base_amount, (v) => setField({ base_amount: v }), 'Fixed amount ($)', '15000')}</div>
          <div className="flex items-center gap-2 flex-wrap"><span className="text-sm text-gray-500">plus</span>{rateInput(d.bonus_rate, (v) => setField({ bonus_rate: v }), 'Bonus rate (%)')}
            <span className="text-sm text-gray-500">% of any amount between</span>{dollarInput(d.reserve, (v) => setField({ reserve: v }), 'Reserve ($)', '800000')}<span className="text-sm text-gray-500">and the sale price</span></div>
        </>
      )}
      {method === 'fixed_percentage_reserve' && (
        <>
          <div className="flex items-center gap-2 flex-wrap">{rateInput(d.base_rate, (v) => setField({ base_rate: v }), 'Base rate (%)')}
            <span className="text-sm text-gray-500">% of the sale price</span></div>
          <div className="flex items-center gap-2 flex-wrap"><span className="text-sm text-gray-500">plus</span>{rateInput(d.bonus_rate, (v) => setField({ bonus_rate: v }), 'Bonus rate (%)')}
            <span className="text-sm text-gray-500">% of any amount between</span>{dollarInput(d.reserve, (v) => setField({ reserve: v }), 'Reserve ($)', '800000')}<span className="text-sm text-gray-500">and the sale price</span></div>
        </>
      )}
      {method === 'vendor_discretion' && (
        <div className="flex items-center gap-2 flex-wrap"><span className="text-sm text-gray-500">Between</span>{rateInput(d.lower, (v) => setField({ lower: v }), 'Lower limit (%)')}
          <span className="text-sm text-gray-500">% and</span>{rateInput(d.upper, (v) => setField({ upper: v }), 'Upper limit (%)')}
          <span className="text-sm text-gray-500">% of the sale price, at the vendor&rsquo;s discretion</span></div>
      )}
      {method === 'upload' && (
        <>
          <textarea disabled={disabled} value={d.wording || ''} onChange={(e) => setField({ wording: e.target.value })}
            rows={3} placeholder="Describe the commission structure as it should appear on the authority."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40 bg-white disabled:bg-gray-50" />
          <FileDropQuestion envelopeId={envelopeId} fieldKey="performance_commission_file" disabled={disabled} />
        </>
      )}

      {/* Commission example: live estimate, like Real Time Agent. */}
      {method && method !== 'upload' && (
        <div className="pt-3 border-t border-gray-200 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
          <label className="flex items-center gap-2"><span className="text-gray-600">If sold at $</span>
            <input type="text" inputMode="decimal" disabled={disabled} value={d.example || ''}
              onChange={(e) => setField({ example: num(e.target.value) })} placeholder="1000000" aria-label="Example sale price ($)"
              className={field} /></label>
          {est != null && (typeof est === 'object'
            ? <span className="text-gray-700">Estimated commission: <b>${pcMoney(est.low)}</b> – <b>${pcMoney(est.high)}</b></span>
            : <span className="text-gray-700">Estimated commission: <b>${pcMoney(est)}</b>{gst === 'exc' && <> &middot; incl. GST <b>${pcMoney(estIncGST(est))}</b></>}</span>)}
        </div>
      )}
      <p className="text-[11px] text-gray-400">Optional &mdash; leave the method on &ldquo;Standard commission&rdquo; to use the normal fixed/percentage fields.</p>
    </div>
  );
}


function DayMultiSelect({ options, value, disabled, onChange, className }) {
  const parts = (value || '').split(',').map((s) => s.trim()).filter(Boolean);
  const presetStrs = options.map(String);
  const ticked = new Set(parts.filter((p) => presetStrs.includes(p)));
  const custom = parts.filter((p) => !presetStrs.includes(p)).join(', ');

  const rebuild = (nextTicked, nextCustom) => {
    const ordered = presetStrs.filter((p) => nextTicked.has(p));
    if (nextCustom.trim()) ordered.push(nextCustom.trim());
    onChange(ordered.join(', '));
  };

  const toggle = (opt) => {
    const next = new Set(ticked);
    if (next.has(opt)) next.delete(opt); else next.add(opt);
    rebuild(next, custom);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {presetStrs.map((opt) => (
        <label key={opt} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm cursor-pointer ${
          ticked.has(opt) ? 'border-teal-600 bg-teal-50 text-teal-700 font-semibold' : 'border-gray-300 text-gray-600 hover:bg-gray-50'} ${
          disabled ? 'opacity-60 cursor-not-allowed' : ''}`}>
          <input type="checkbox" className="hidden" checked={ticked.has(opt)} disabled={disabled} onChange={() => toggle(opt)} />
          {opt}
        </label>
      ))}
      <input type="text" placeholder="Custom" disabled={disabled} value={custom}
        onChange={(e) => rebuild(ticked, e.target.value)}
        className={`${className} w-28`} />
    </div>
  );
}

// --- Supporting-document drop zone (capture-only "file" questions) ---
const ATTACHMENT_MAX_MB = 10;

function FileDropQuestion({ envelopeId, fieldKey, disabled }) {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dropActive, setDropActive] = useState(false);

  useEffect(() => {
    let live = true;
    esignApi.listAttachments(envelopeId, fieldKey)
      .then(({ data }) => { if (live) setAttachments(Array.isArray(data) ? data : []); })
      .catch(() => {})
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [envelopeId, fieldKey]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > ATTACHMENT_MAX_MB * 1024 * 1024) {
      toast.error(`"${file.name}" is over ${ATTACHMENT_MAX_MB} MB.`);
      return;
    }
    setUploading(true);
    try {
      const { data } = await esignApi.uploadAttachment(envelopeId, fieldKey, file);
      setAttachments((prev) => [data, ...prev]);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not upload the file.');
    } finally {
      setUploading(false);
    }
  };

  const remove = async (attachmentId) => {
    try {
      await esignApi.removeAttachment(envelopeId, attachmentId);
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch {
      toast.error('Could not remove the file.');
    }
  };

  return (
    <div>
      {!disabled && (
        <label
          onDragOver={(e) => { e.preventDefault(); setDropActive(true); }}
          onDragLeave={() => setDropActive(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDropActive(false);
            upload(e.dataTransfer?.files?.[0]);
          }}
          className={`flex flex-col items-center justify-center gap-1 border-2 border-dashed rounded-lg py-5 px-4 cursor-pointer transition-colors ${
            dropActive ? 'border-teal-500 bg-teal-50' : 'border-gray-300 hover:border-gray-400'} ${
            uploading ? 'opacity-60 pointer-events-none' : ''}`}>
          {uploading ? <Loader2 className="w-5 h-5 text-gray-400 animate-spin" /> : <Upload className="w-5 h-5 text-gray-400" />}
          <span className="text-sm text-gray-600">Drag a file here, or click to choose</span>
          <span className="text-xs text-gray-400">Up to {ATTACHMENT_MAX_MB} MB</span>
          <input type="file" className="hidden" disabled={uploading}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; upload(f); }} />
        </label>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-gray-400 mt-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
        </div>
      ) : attachments.length > 0 && (
        <div className="flex flex-col gap-1.5 mt-2">
          {attachments.map((a) => (
            <div key={a.id} className="flex items-center gap-2 text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5">
              <Paperclip className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              {a.file_url ? (
                <a href={a.file_url} target="_blank" rel="noreferrer"
                  className="text-teal-700 hover:underline truncate flex-1">{a.original_name}</a>
              ) : (
                <span className="truncate flex-1">{a.original_name}</span>
              )}
              {!disabled && (
                <button type="button" onClick={() => remove(a.id)}
                  className="text-gray-400 hover:text-red-600 shrink-0" aria-label={`Remove ${a.original_name}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Licence scan (vendor tab) ---
function LicenceScanButton({ id, onScanned }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const { data } = await esignApi.scanLicence(id, file);
      const fields = data?.fields || {};
      if (Object.values(fields).some(Boolean)) {
        onScanned(fields);
        toast.success('Vendor details read from licence.');
      } else {
        toast('Could not read details — please type them in.', { icon: 'ℹ️' });
      }
    } catch (err) {
      const msg = err?.response?.status === 501
        ? 'Licence scanning is not enabled on the server.'
        : 'Could not read the licence.';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} />
      <button onClick={() => ref.current?.click()} disabled={busy}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border text-teal-700 hover:bg-teal-50 disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />}
        Scan licence
      </button>
    </>
  );
}

// --- Legal representation contact picker ---
function LegalRepPicker({ onPick }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [matches, setMatches] = useState([]);
  const timer = useRef();

  const run = (text) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      if (!text || text.trim().length < 2) { setMatches([]); return; }
      try {
        // Saved directory (firms other agents entered before) first, then CRM
        // contacts — ticket 12525266778: "save the details for the next agents use".
        const [saved, contacts] = await Promise.all([
          esignApi.conveyancerSuggestions(text).then((r) => r.data).catch(() => []),
          getContacts({ search: text, page_size: 8 })
            .then((r) => (Array.isArray(r.data) ? r.data : (r.data.results || []))).catch(() => []),
        ]);
        // One entry per conveyancer, keyed on email identity (else name), keeping
        // the record that carries a firm so the saved directory's firm wins over a
        // firm-less CRM contact instead of both showing (DOMA 13145698774).
        const byKey = new Map();
        [...(saved || []), ...(contacts || [])].forEach((m) => {
          if (!m.name) return;
          const key = (m.email || '').trim().toLowerCase() || (m.name || '').trim().toLowerCase();
          if (!key) return;
          const firm = (m.firm || m.company || '').trim();
          const existing = byKey.get(key);
          if (!existing || (firm && !(existing.firm || existing.company || '').trim())) {
            byKey.set(key, m);
          }
        });
        setMatches([...byKey.values()].slice(0, 10));
      } catch { setMatches([]); }
    }, 250);
  };

  return (
    <div className="relative mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Find solicitor / conveyancer <span className="text-gray-400 font-normal">(fills the details below)</span>
      </label>
      <div className="relative">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} placeholder="Search saved firms or contacts by name"
          onChange={(e) => { setQ(e.target.value); setOpen(true); run(e.target.value); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          className="w-full border border-gray-300 rounded-lg pl-9 pr-8 py-2 text-sm" />
        {q && <button onClick={() => { setQ(''); setMatches([]); }}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"><X className="w-4 h-4" /></button>}
      </div>
      {open && matches.length > 0 && (
        <div className="absolute z-40 left-0 right-0 top-full mt-1 bg-white border rounded-lg shadow-lg max-h-52 overflow-y-auto">
          {matches.map((m, i) => {
            // Show the conveyancer's firm/company name first; fall back to the
            // contact/practitioner name or email when no firm is on record.
            const firm = m.firm || m.company || '';
            const primary = firm || m.name || '—';
            const secondary = firm ? (m.name || m.email || '') : (m.email || '');
            return (
              <button type="button" key={m.id ?? i} onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onPick(m); setOpen(false); setQ(m.name || ''); }}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm truncate">
                {primary}{secondary ? ` · ${secondary}` : ''}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
